import { useCallback, useEffect, useRef } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { getFilterParams } from '@/metrics/helpers/helpers'

import { exportFormats } from './constants'
import { getEntityObject } from './helpers'

const useMetrics = ({
  returnPortalId,
  storeId,
  service,
  filters,
  additionalFilters = null,
  entityId,
  entityType,
  doRequest = true,
}) => {
  const { data, fetchData, cancel, loading, error } = useFetch(service, {
    doInitialRequest: false,
  })

  const prevFilters = useRef()
  const prevAdditionalFilters = useRef()
  const prevEntityId = useRef()
  const prevEntityType = useRef()

  const hasChanges = useCallback(() => {
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(prevFilters.current)
    const additionalFiltersChanged = JSON.stringify(additionalFilters) !== JSON.stringify(prevAdditionalFilters.current)
    const entityIdChanged = entityId !== prevEntityId.current
    const entityTypeChanged = entityType !== prevEntityType.current

    return filtersChanged || additionalFiltersChanged || entityIdChanged || entityTypeChanged
  }, [filters, additionalFilters, entityId, entityType])

  const refetchWhenChangeStore = () => {
    if (doRequest && hasChanges()) {
      prevFilters.current = filters
      prevAdditionalFilters.current = additionalFilters
      prevEntityId.current = entityId
      prevEntityType.current = entityType

      cancel()
      fetchData({
        returnPortalId,
        storeId,
        ...getEntityObject(entityId, entityType),
        params: getFilterParams(additionalFilters ? { ...filters, ...additionalFilters } : { ...filters }),
      })
    }
  }

  useEffect(refetchWhenChangeStore, [
    additionalFilters,
    cancel,
    fetchData,
    filters,
    returnPortalId,
    storeId,
    entityId,
    entityType,
    doRequest,
    hasChanges,
  ])

  const exportData = async (formatType, fileName) => {
    const response = await service({
      returnPortalId,
      storeId,
      params: getFilterParams(additionalFilters ? { ...filters, ...additionalFilters } : { ...filters }),
      headers: { accept: exportFormats[formatType] },
      responseType: 'arraybuffer',
    })

    const blob = new Blob([response?.data], {
      type: exportFormats[formatType],
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')

    a.href = url

    a.download = `${fileName}${new Date().getTime()}.${formatType}`
    a.click()
  }

  return {
    data,
    loading,
    error,
    exportData,
  }
}

export default useMetrics
